import { useEffect, useState } from 'react'
import { useCreateZone, useDeleteZone, useUpdateZone } from '../../api/hooks'
import type { Zone } from '../../api/types'
import { ZONE_KINDS } from '../../lib/zones'
import { Button, ConfirmDeleteModal, Input, Modal } from '../ui'
import { LocationMultiPicker } from './LocationMultiPicker'

function errText(e: unknown): string {
  return e instanceof Error ? e.message : String(e)
}

/**
 * Create or edit a zone (alarm area, anti-passback, …). A zone spans any
 * number of locations at any tree level — several addresses, several
 * floors — and covers everything beneath each one. Saving full-replaces
 * the location set (PUT /zones/{uid}).
 *
 * `zone` null = create, with `initialLocations` pre-selected.
 */
export function ZoneEditorModal({
  open,
  zone,
  initialLocations = [],
  onClose,
}: {
  open: boolean
  zone: Zone | null
  initialLocations?: string[]
  onClose: () => void
}) {
  const create = useCreateZone()
  const update = useUpdateZone()
  const remove = useDeleteZone()

  const [name, setName] = useState('')
  const [kind, setKind] = useState<string>('alarm_area')
  const [locations, setLocations] = useState<string[]>([])
  const [confirmDelete, setConfirmDelete] = useState(false)

  // Reset the form each time the modal (re)opens.
  useEffect(() => {
    if (!open) return
    setName(zone?.name ?? '')
    setKind(zone?.kind ?? 'alarm_area')
    setLocations(zone ? [...zone.locations] : [...initialLocations])
    setConfirmDelete(false)
    create.reset()
    update.reset()
    remove.reset()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset on open only
  }, [open, zone])

  const saving = create.isPending || update.isPending
  const error = create.error ?? update.error
  const save = () => {
    const body = { kind, name: name.trim(), locations }
    if (zone) update.mutate({ uid: zone.unique_id, body }, { onSuccess: onClose })
    else create.mutate(body, { onSuccess: onClose })
  }

  if (zone && confirmDelete) {
    return (
      <ConfirmDeleteModal
        open={open}
        title="Delete zone"
        objectName={zone.name}
        expected={zone.unique_id}
        pending={remove.isPending}
        error={remove.error ? errText(remove.error) : null}
        onConfirm={() => remove.mutate(zone.unique_id, { onSuccess: onClose })}
        onClose={() => setConfirmDelete(false)}
      >
        <p>
          Delete zone <strong>{zone.name}</strong>? It moves to Trash and can be restored from there.
        </p>
      </ConfirmDeleteModal>
    )
  }

  return (
    <Modal open={open} title={zone ? `Edit zone · ${zone.name}` : 'New zone'} onClose={onClose}>
      <div className="flex flex-col gap-4">
        <Input label="Name" value={name} onChange={(e) => setName(e.target.value)} />
        {zone ? null : (
          <div className="flex flex-col gap-1.5">
            <label
              htmlFor="zone-kind"
              className="text-[10px] font-semibold uppercase tracking-widest text-ostrel-ink-muted"
            >
              Kind
            </label>
            <select
              id="zone-kind"
              value={kind}
              onChange={(e) => setKind(e.target.value)}
              className="rounded-sm border border-ostrel-line bg-ostrel-card px-3 py-2 text-sm text-ostrel-ink focus:border-ostrel-gold focus:outline-none focus:ring-1 focus:ring-ostrel-gold"
            >
              {ZONE_KINDS.map((k) => (
                <option key={k.value} value={k.value}>
                  {k.label}
                </option>
              ))}
            </select>
          </div>
        )}

        <div className="flex flex-col gap-1.5">
          <span className="text-[10px] font-semibold uppercase tracking-widest text-ostrel-ink-muted">
            Locations ({locations.length})
          </span>
          <p className="text-[11px] text-ostrel-ink-muted">
            A zone can span several addresses, floors or rooms; each covers everything beneath it.
          </p>
          <LocationMultiPicker value={locations} onChange={setLocations} label="Zone locations" />
        </div>

        {error && <p className="text-xs text-ostrel-danger">{errText(error)}</p>}
        <div className="flex flex-wrap gap-2">
          <Button variant="navy" disabled={saving || !name.trim()} onClick={save}>
            {saving ? 'Saving…' : zone ? 'Save zone' : 'Create zone'}
          </Button>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          {zone && (
            <Button variant="danger" className="ml-auto" onClick={() => setConfirmDelete(true)}>
              Delete
            </Button>
          )}
        </div>
      </div>
    </Modal>
  )
}
